import api from './api';
import { Schwachstelle, SchwachstelleCreate, SchwachstelleList, SchwachstelleLockResponse, SchwachstelleKommentar, ReviewQueueItem, FileEntity, TransferTargetKunde } from '../types';

export interface DetailbeschreibungAiContext {
  schwachstelle_id?: number | null;
  titel: string;
  detailbeschreibung?: string;
  allgemeine_beschreibung?: string;
  kritikalitaet?: string;
  cvss31_score?: number;
  cvss31_vector?: string;
  host?: string;
  ip_adresse?: string;
  schwachstellenklasse?: string;
  cwe?: string;
  tools: string[];
  risikoszenarien: string[];
}

export interface DetailbeschreibungAiRealtimeSessionResponse {
  session_id?: string | null;
  client_secret_value: string;
  client_secret_expires_at?: number | null;
  model: string;
  voice: string;
  prompt_id: string;
}

export interface DetailbeschreibungAiDraftResponse {
  html: string;
}

export const schwachstellenService = {
  list: async (params?: { audit_id?: number; kritikalitaet?: string; limit?: number }): Promise<SchwachstelleList[]> => {
    const response = await api.get('/schwachstellen/', { params });
    return response.data;
  },

  get: async (id: number): Promise<Schwachstelle> => {
    const response = await api.get(`/schwachstellen/${id}`);
    return response.data;
  },

  create: async (data: SchwachstelleCreate): Promise<Schwachstelle> => {
    const response = await api.post('/schwachstellen/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<SchwachstelleCreate>): Promise<Schwachstelle> => {
    const response = await api.put(`/schwachstellen/${id}`, data);
    return response.data;
  },

  reorder: async (audit_id: number, ordered_ids: number[]): Promise<SchwachstelleList[]> => {
    const response = await api.put('/schwachstellen/reorder', { audit_id, ordered_ids });
    return response.data;
  },

  transferTargets: async (excludeAuditId?: number): Promise<TransferTargetKunde[]> => {
    const response = await api.get('/schwachstellen/transfer-targets', {
      params: excludeAuditId != null ? { exclude_audit_id: excludeAuditId } : undefined,
    });
    return response.data;
  },

  transfer: async (id: number, zielAuditId: number): Promise<Schwachstelle> => {
    const response = await api.post(`/schwachstellen/${id}/transfer`, { ziel_audit_id: zielAuditId });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/schwachstellen/${id}`);
  },

  getFilesToDelete: async (id: number): Promise<FileEntity[]> => {
    const response = await api.get(`/schwachstellen/${id}/files-to-delete`);
    return response.data;
  },

  statusTransition: async (id: number, status_id: number, kommentar?: string): Promise<Schwachstelle> => {
    const response = await api.post(`/schwachstellen/${id}/status-transition`, { status_id, kommentar });
    return response.data;
  },

  generateReport: async (id: number): Promise<void> => {
    const response = await api.post(`/schwachstellen/${id}/report`, null, {
      responseType: 'blob',
    });
    // Extract filename from Content-Disposition header or use fallback
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : `schwachstelle_${id}_report.pdf`;

    // Trigger browser download
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  lock: async (id: number): Promise<SchwachstelleLockResponse> => {
    const response = await api.post(`/schwachstellen/${id}/lock`);
    return response.data;
  },

  unlock: async (id: number): Promise<void> => {
    await api.post(`/schwachstellen/${id}/unlock`);
  },

  forceUnlock: async (id: number): Promise<void> => {
    await api.post(`/schwachstellen/${id}/force-unlock`);
  },

  heartbeat: async (id: number): Promise<SchwachstelleLockResponse> => {
    const response = await api.post(`/schwachstellen/${id}/heartbeat`);
    return response.data;
  },

  reviewQueue: async (): Promise<ReviewQueueItem[]> => {
    const response = await api.get('/schwachstellen/review-queue');
    return response.data;
  },

  reviewQueueCount: async (): Promise<number> => {
    const response = await api.get('/schwachstellen/review-queue/count');
    return response.data.count;
  },

  claimReview: async (id: number): Promise<Schwachstelle> => {
    const response = await api.post(`/schwachstellen/${id}/claim-review`);
    return response.data;
  },

  getKommentare: async (id: number): Promise<SchwachstelleKommentar[]> => {
    const response = await api.get(`/schwachstellen/${id}/kommentare`);
    return response.data;
  },

  createKommentar: async (id: number, inhalt: string): Promise<SchwachstelleKommentar> => {
    const response = await api.post(`/schwachstellen/${id}/kommentare`, { inhalt });
    return response.data;
  },

  updateKommentar: async (schwachstelleId: number, kommentarId: number, inhalt: string): Promise<SchwachstelleKommentar> => {
    const response = await api.put(`/schwachstellen/${schwachstelleId}/kommentare/${kommentarId}`, { inhalt });
    return response.data;
  },

  deleteKommentar: async (schwachstelleId: number, kommentarId: number): Promise<void> => {
    await api.delete(`/schwachstellen/${schwachstelleId}/kommentare/${kommentarId}`);
  },

  createDetailbeschreibungRealtimeSession: async (
    context: DetailbeschreibungAiContext
  ): Promise<DetailbeschreibungAiRealtimeSessionResponse> => {
    const response = await api.post('/schwachstellen/ai/detailbeschreibung/realtime-session', { context });
    return response.data;
  },

  generateDetailbeschreibungDraft: async (
    context: DetailbeschreibungAiContext,
    transcript: string
  ): Promise<DetailbeschreibungAiDraftResponse> => {
    const response = await api.post('/schwachstellen/ai/detailbeschreibung/draft', { context, transcript });
    return response.data;
  },
};
